import { useMemo } from "react";
import { useJobs } from "@/hooks/useJobs";
import { usePipelines } from "@/hooks/usePipeline";
import { CANONICAL_STATES } from "@career-ops-amplify/shared";
import {
  computePipelineAnalytics,
  computeWeeklyActivity,
  topCompanies,
} from "@/lib/pipeline-analytics";
import { cn } from "@/cn";

const STAGE_COLORS: Record<string, string> = {
  Evaluated: "bg-zinc-500",
  Applied: "bg-sky-500",
  Responded: "bg-sky-400",
  Interview: "bg-emerald-400",
  Offer: "bg-emerald-500",
  Rejected: "bg-red-400",
  Discarded: "bg-zinc-600",
  SKIP: "bg-zinc-600",
};

const SCORE_LABELS = ["4+", "3-4", "2-3", "1-2", "0-1"] as const;

export function AnalyticsPage() {
  const { data: jobsData, isLoading: jobsLoading, error: jobsError } = useJobs();
  const { data: pipelinesData, isLoading: pipelinesLoading } = usePipelines();

  const jobs = jobsData?.jobs ?? [];
  const pipelines = pipelinesData?.pipelines ?? [];
  const isLoading = jobsLoading || pipelinesLoading;

  const stats = useMemo(
    () => computePipelineAnalytics(jobs, pipelines),
    [jobs, pipelines],
  );

  const weekly = useMemo(() => computeWeeklyActivity(jobs), [jobs]);
  const companies = useMemo(() => topCompanies(jobs, pipelines), [jobs, pipelines]);
  const maxScoreBucket = Math.max(...stats.scoreBuckets, 1);
  const maxWeekly = Math.max(...weekly.values, 1);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-brand" />
          <p className="text-sm text-muted-foreground">Loading analytics…</p>
        </div>
      </div>
    );
  }

  if (jobsError) {
    return (
      <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3">
        <p className="text-sm text-destructive">Failed to load analytics data.</p>
      </div>
    );
  }

  return (
    <>
      <header className="mb-8">
        <h1 className="text-2xl font-bold text-foreground">Analytics</h1>
        <p className="text-muted-foreground">
          Pipeline stats, conversion rates, and performance insights.
        </p>
      </header>

      {/* Overview — expands home dashboard aggregates */}
      <div className="mb-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7">
        <StatCard label="Total Jobs" value={String(stats.totalJobs)} />
        <StatCard label="In Pipeline" value={String(stats.inPipeline)} />
        <StatCard label="Active" value={String(stats.active)} />
        <StatCard label="Interviews" value={String(stats.interviews)} />
        <StatCard label="Offers" value={String(stats.offers)} />
        <StatCard
          label="Unprocessed"
          value={String(stats.unprocessed)}
          highlight={stats.unprocessed > 0}
        />
        <StatCard label="Avg. Score" value={stats.avgScore} />
      </div>

      <div className="mb-8 grid gap-6 lg:grid-cols-2">
        {/* Pipeline funnel by stage */}
        <div className="rounded-lg border border-border bg-card p-5">
          <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Pipeline Funnel
          </h2>
          <div className="space-y-3">
            {CANONICAL_STATES.map((stage) => (
              <FunnelRow
                key={stage}
                label={stage === "SKIP" ? "Skipped" : stage}
                count={stats.stageCounts[stage] ?? 0}
                total={stats.inPipeline}
                color={STAGE_COLORS[stage] ?? "bg-zinc-500"}
              />
            ))}
            {stats.unprocessed > 0 && (
              <FunnelRow
                label="Pending"
                count={stats.unprocessed}
                total={stats.totalJobs}
                color="bg-amber-500"
              />
            )}
          </div>
        </div>

        {/* Score distribution — same buckets as home */}
        <div className="rounded-lg border border-border bg-card p-5">
          <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Score Distribution
          </h2>
          <div className="space-y-2.5">
            {SCORE_LABELS.map((label, i) => {
              const idx = 4 - i;
              const count = stats.scoreBuckets[idx];
              const pct = Math.round((count / maxScoreBucket) * 100);
              return (
                <div key={label} className="flex items-center gap-3">
                  <span className="w-8 text-right text-xs text-muted-foreground">
                    {label}
                  </span>
                  <div className="flex-1">
                    <div className="h-4 overflow-hidden rounded-full bg-muted/20">
                      <div
                        className={cn(
                          "h-full rounded-full transition-all",
                          idx >= 4
                            ? "bg-emerald-500"
                            : idx >= 3
                              ? "bg-amber-500"
                              : "bg-red-500",
                        )}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                  <span className="w-6 text-right text-xs text-muted-foreground">
                    {count}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Conversion rates */}
      <div className="mb-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <RateCard
          label="Apply Rate"
          value={`${stats.applyRate}%`}
          description="Applied / Evaluated"
        />
        <RateCard
          label="Response Rate"
          value={`${stats.responseRate}%`}
          description="Responded / Submitted"
        />
        <RateCard
          label="Interview Rate"
          value={`${stats.interviewRate}%`}
          description="Interviews / Submitted"
        />
        <RateCard
          label="Offer Rate"
          value={`${stats.offerRate}%`}
          description="Offers / Submitted"
        />
      </div>

      <div className="mb-8 grid gap-6 lg:grid-cols-2">
        {/* Weekly activity */}
        <div className="rounded-lg border border-border bg-card p-5">
          <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Weekly Activity
          </h2>
          {weekly.labels.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No activity data available yet.
            </p>
          ) : (
            <div className="flex items-end gap-2" style={{ height: 120 }}>
              {weekly.labels.map((label, i) => {
                const value = weekly.values[i] ?? 0;
                const pct = Math.round((value / maxWeekly) * 100);
                return (
                  <div
                    key={label}
                    className="flex flex-1 flex-col items-center gap-1"
                  >
                    <span className="text-xs text-muted-foreground">{value}</span>
                    <div
                      className="w-full rounded-sm bg-brand transition-all"
                      style={{
                        height: `${Math.max(pct, 4)}%`,
                        minHeight: 4,
                      }}
                    />
                    <span className="text-[10px] text-muted-foreground">
                      {label}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Top companies */}
        <div className="rounded-lg border border-border bg-card p-5">
          <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Top Companies
          </h2>
          {companies.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No pipeline jobs yet.
            </p>
          ) : (
            <div className="space-y-3">
              {companies.map(({ company, count }) => (
                <div key={company} className="flex items-center gap-3">
                  <span className="flex-1 truncate text-sm text-foreground">
                    {company}
                  </span>
                  <div className="w-24">
                    <div className="h-2 overflow-hidden rounded-full bg-muted/20">
                      <div
                        className="h-full rounded-full bg-brand"
                        style={{
                          width: `${Math.round((count / companies[0].count) * 100)}%`,
                        }}
                      />
                    </div>
                  </div>
                  <span className="w-6 text-right text-xs text-muted-foreground">
                    {count}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function StatCard({
  label,
  value,
  highlight,
}: {
  label: string;
  value: string;
  highlight?: boolean;
}) {
  return (
    <div
      className={cn(
        "rounded-lg border bg-card p-4",
        highlight ? "border-brand/30 bg-brand/[0.03]" : "border-border",
      )}
    >
      <p className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <p className="mt-1 text-2xl font-bold text-foreground">{value}</p>
    </div>
  );
}

function FunnelRow({
  label,
  count,
  total,
  color,
}: {
  label: string;
  count: number;
  total: number;
  color: string;
}) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div className="flex items-center gap-3">
      <div className="flex items-center gap-2" style={{ width: 100 }}>
        <span className={cn("inline-block h-2.5 w-2.5 rounded-full", color)} />
        <span className="text-sm text-foreground">{label}</span>
      </div>
      <div className="flex-1">
        <div className="h-3 overflow-hidden rounded-full bg-muted/20">
          <div
            className={cn("h-full rounded-full", color)}
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>
      <span className="w-16 text-right text-sm text-muted-foreground">
        {count}
        <span className="text-xs"> ({pct}%)</span>
      </span>
    </div>
  );
}

function RateCard({
  label,
  value,
  description,
}: {
  label: string;
  value: string;
  description: string;
}) {
  const numVal = parseFloat(value);
  const tone =
    numVal >= 30
      ? "text-emerald-400"
      : numVal >= 15
        ? "text-amber-400"
        : "text-red-400";

  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <p className={cn("mt-1 text-3xl font-bold", tone)}>{value}</p>
      <p className="mt-1 text-xs text-muted-foreground">{description}</p>
    </div>
  );
}
